import type { Ref } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { SHELL_CHROME_VAR, sumChromeHeight } from '../shell/chromeHeight.js';

/**
 * 139 — measures the shell's own furniture and publishes the total as `--shell-chrome-h`, which
 * `index.css`'s `.page-fill` / `.page-bound` subtract from `100dvh`. One property, so there is
 * exactly one place a screen's available height comes from (FR-005). Feature 138's separate
 * `--install-offer-h` is absorbed: the offer now reports its height here instead of publishing its
 * own.
 *
 * 🔴 `useLayoutEffect`, not `useEffect`. The first publish has to land before paint, or every
 * screen renders one frame at the wrong height — a visible flash on load.
 *
 * The heights are measured rather than stated because they are not constants: the impersonation
 * banner measures 40px at 1440px wide and 76px at 390px (078's own finding), and the install offer
 * wraps to two lines at 375px. The bottom-nav reserve is the exception — it is a reserve, not an
 * element, so the shell contributes it directly and only when the navigation is shown.
 */
const BOTTOM_NAV_RESERVE_PX = 80; // `pb-20` on `app-content` in `main.tsx`

/**
 * 🔴 `getBoundingClientRect().height`, not `offsetHeight`. `offsetHeight` is rounded to a whole
 * pixel, so on a device whose device-pixel-ratio is not 1 — most phones — a 40.4px notice reports
 * 40, the shell under-reserves by 0.4px, and the page gains a sliver of phantom scroll. That is
 * precisely the defect this feature exists to remove, just under a pixel, and a CSS-pixel test
 * viewport cannot surface it. `sumChromeHeight` rounds the TOTAL up for the same reason.
 */
function measure(el: HTMLElement | null): number {
  return el ? el.getBoundingClientRect().height : 0;
}

export interface ShellChromeOptions {
  /** The shell's own `showNav` — decides the nav reserve, and whether the bell can render at all. */
  navVisible: boolean;
  /** Height reported by the install offer; 0 when it is not showing. */
  offerPx: number;
}

export interface ShellChromeHandles {
  noticesRef: Ref<HTMLDivElement>;
  bellRef: Ref<HTMLDivElement>;
}

export function useShellChromeHeight({
  navVisible,
  offerPx,
}: ShellChromeOptions): ShellChromeHandles {
  const noticesRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;

    const publish = () => {
      root.style.setProperty(
        SHELL_CHROME_VAR,
        `${sumChromeHeight({
          noticesPx: measure(noticesRef.current),
          bellPx: measure(bellRef.current),
          navReservePx: navVisible ? BOTTOM_NAV_RESERVE_PX : 0,
          offerPx,
        })}px`,
      );
    };

    publish();

    // Guarded exactly as `PickerPanel.tsx:76` guards `matchMedia`: jsdom implements no
    // `ResizeObserver`, and neither do some embedded WebViews. Without an observer the figure is
    // still correct at mount and on every re-render that changes `navVisible` or `offerPx` — it
    // simply will not follow a banner that rewraps.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(publish);
      // 🔴 `border-box`, because `offsetHeight` above IS the border box. Observing the default
      // content-box while measuring the border box means padding and border changes never fire:
      // found live, by growing a notice's padding and watching the published figure not move,
      // while the element itself had visibly grown. Text rewrapping would have fired either way,
      // which is why this could have shipped unnoticed.
      const options: ResizeObserverOptions = { box: 'border-box' };
      if (noticesRef.current) observer.observe(noticesRef.current, options);
      if (bellRef.current) observer.observe(bellRef.current, options);
    }

    return () => {
      observer?.disconnect();
      root.style.removeProperty(SHELL_CHROME_VAR);
    };
  }, [navVisible, offerPx]);

  return { noticesRef, bellRef };
}
